import useSWR from "swr";
import { VaultFolder } from "@/types";
import { listFolders, getFolder } from "@/services/storageManager";
import { API_ROUTES } from "@/utils/constants";

export function useFolders(parentId: string | null = null) {
  const key = `${API_ROUTES.folders}?parentId=${parentId || ""}`;

  const { data, error, isLoading, mutate } = useSWR(
    key,
    () => listFolders({ parentId }),
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  );

  return {
    folders: data || [],
    isLoading,
    isError: error,
    mutate,
  };
}

export function useFolder(folderId: string | null) {
  const { data, error, isLoading, mutate } = useSWR(
    folderId ? API_ROUTES.folder(folderId) : null,
    () => getFolder(folderId!),
    {
      revalidateOnFocus: false,
    }
  );

  return {
    folder: data,
    isLoading,
    isError: error,
    mutate,
  };
}

export function useBreadcrumb(folderId: string | null) {
  // Build breadcrumb by fetching folder hierarchy
  const { data, error, isLoading } = useSWR(
    folderId ? `breadcrumb-${folderId}` : "breadcrumb-root",
    async () => {
      if (!folderId) return [];

      const breadcrumb: VaultFolder[] = [];
      let currentId: string | null = folderId;

      // Walk up the folder tree
      while (currentId) {
        const folder = await getFolder(currentId);
        if (!folder) break;
        breadcrumb.unshift(folder);
        currentId = folder.parentFolderId;
      }

      return breadcrumb;
    },
    {
      revalidateOnFocus: false,
    }
  );

  return {
    breadcrumb: data || [],
    isLoading,
    isError: error,
  };
}

export function useFolderTree() {
  const { data, error, isLoading } = useSWR(
    "folder-tree",
    async () => {
      // Build a tree structure from flat folders
      const buildTree = async (
        parentId: string | null
      ): Promise<(VaultFolder & { children?: VaultFolder[] })[]> => {
        const folders = await listFolders({ parentId });
        const result = await Promise.all(
          folders.map(async (folder) => ({
            ...folder,
            children: await buildTree(folder.id),
          }))
        );
        return result;
      };
      return buildTree(null);
    },
    {
      revalidateOnFocus: false,
    }
  );

  return {
    tree: data || [],
    isLoading,
    isError: error,
  };
}
